/*
  Standalone relay reader for the web target.

  The napplet never does this: NAP-OUTBOX owns relay discovery, routing,
  deduplication and validation in the shell, so the napplet host just calls
  `outbox.query` / `outbox.subscribe`. A plain Vite build has no shell, so it
  needs its own NIP-01 client — a deliberately small one: fixed default relays,
  REQ/CLOSE, dedupe by event id, filter match, and an id-hash check before an
  event is trusted. No signing, no publishing, no relay discovery.
*/
import type { EventSubscription, NostrEvent, NostrFilter, RelayEventResult } from "../core/nostr";

/** Relays the standalone build reads from by default. */
export const DEFAULT_RELAYS: readonly string[] = [
  "wss://nos.lol",
  "wss://relay.primal.net",
  "wss://relay.routstr.com"
];

/**
 * Wider set consulted when the default relays return nothing for a tree.
 * Relays drop, purge old events and rate-limit; a read-only viewer that can
 * only ever ask one relay pair is a viewer that goes blank with the relay.
 */
export const FALLBACK_RELAYS: readonly string[] = ["wss://nostr.mom", "wss://relay.damus.io"];

export const DEFAULT_QUERY_TIMEOUT_MS = 8000;

export interface RelayQueryOptions {
  timeoutMs?: number;
}

export interface RelayWatchHandlers {
  onEvent(result: RelayEventResult): void;
  /** Every relay dropped; the caller should offer a reload. */
  onClosed?(reason: string): void;
  onNotice?(message: string): void;
}

export type RelayFrame =
  | { type: "event"; subscriptionId: string; event: NostrEvent }
  | { type: "eose"; subscriptionId: string }
  | { type: "closed"; subscriptionId: string; reason: string }
  | { type: "notice"; reason: string }
  | { type: "auth"; challenge: string }
  | { type: "other"; label: string };

const asString = (value: unknown) => (typeof value === "string" ? value : "");

export function isNostrEvent(value: unknown): value is NostrEvent {
  if (!value || typeof value !== "object") return false;
  const event = value as Record<string, unknown>;
  return typeof event.id === "string"
    && typeof event.pubkey === "string"
    && typeof event.created_at === "number"
    && typeof event.kind === "number"
    && typeof event.content === "string"
    && typeof event.sig === "string"
    && Array.isArray(event.tags)
    && event.tags.every((tag) => Array.isArray(tag) && tag.every((item) => typeof item === "string"));
}

/** Decode one NIP-01 relay frame. Unknown or malformed frames decode to `undefined`. */
export function decodeRelayFrame(data: unknown): RelayFrame | undefined {
  if (!Array.isArray(data) || typeof data[0] !== "string") return undefined;
  switch (data[0]) {
    case "EVENT": {
      const event = data[2];
      if (!isNostrEvent(event)) return undefined;
      return { type: "event", subscriptionId: asString(data[1]), event };
    }
    case "EOSE":
      return { type: "eose", subscriptionId: asString(data[1]) };
    case "CLOSED":
      return { type: "closed", subscriptionId: asString(data[1]), reason: asString(data[2]) };
    case "NOTICE":
      return { type: "notice", reason: asString(data[1]) };
    case "AUTH":
      return { type: "auth", challenge: asString(data[1]) };
    default:
      return { type: "other", label: data[0] };
  }
}

/** NIP-01 filter matching, narrowed to the fields this app filters on. */
export function matchesFilter(event: NostrEvent, filter: NostrFilter): boolean {
  if (filter.ids && !filter.ids.includes(event.id)) return false;
  if (filter.authors && !filter.authors.includes(event.pubkey)) return false;
  if (filter.kinds && !filter.kinds.includes(event.kind)) return false;
  if (filter.since !== undefined && event.created_at < filter.since) return false;
  if (filter.until !== undefined && event.created_at > filter.until) return false;
  for (const [key, values] of Object.entries(filter)) {
    if (!key.startsWith("#") || !values) continue;
    const wanted = values as string[];
    if (!event.tags.some((tag) => tag[0] === key.slice(1) && wanted.includes(tag[1]))) return false;
  }
  return true;
}

const encoder = new TextEncoder();
const toHex = (buffer: ArrayBuffer) =>
  Array.from(new Uint8Array(buffer), (byte) => byte.toString(16).padStart(2, "0")).join("");

/**
 * Recompute the NIP-01 event id: `sha256(JSON.stringify([0, pubkey, created_at,
 * kind, tags, content]))`. Accepts without checking when the platform has no
 * WebCrypto, which only happens on an insecure origin.
 */
export async function eventIdMatches(event: NostrEvent): Promise<boolean> {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) return true;
  const serialized = JSON.stringify([0, event.pubkey, event.created_at, event.kind, event.tags, event.content]);
  const digest = await subtle.digest("SHA-256", encoder.encode(serialized));
  return toHex(digest) === event.id;
}

/** Structural check, filter match, and hash check before an event is trusted. */
export async function isTrustworthyEvent(event: NostrEvent, filters: readonly NostrFilter[]): Promise<boolean> {
  if (filters.length && !filters.some((filter) => matchesFilter(event, filter))) return false;
  return eventIdMatches(event);
}

let subscriptionCounter = 0;
const nextSubscriptionId = () => `toy-rocket:${++subscriptionCounter}`;

interface Upstream {
  url: string;
  socket: WebSocket;
  /** No more events expected: EOSE, CLOSED, or dropped. */
  done: boolean;
  /** Went down before EOSE. */
  failed: boolean;
}

function parseFrame(data: unknown): unknown {
  if (typeof data !== "string") return undefined;
  try {
    return JSON.parse(data);
  } catch {
    return undefined;
  }
}

function closeAll(upstreams: Iterable<Upstream>, subscriptionId: string) {
  for (const { socket } of upstreams) {
    try {
      if (socket.readyState === WebSocket.OPEN) socket.send(JSON.stringify(["CLOSE", subscriptionId]));
    } catch {
      // The socket can drop between the readyState check and the send; a failed
      // CLOSE on a dying socket is not worth reporting.
    }
    socket.close();
  }
}

interface UpstreamHandlers {
  onFrame(upstream: Upstream, frame: RelayFrame): void;
  /** The socket went away (relay CLOSED, error, or transport close). */
  onDown(upstream: Upstream, reason: string): void;
}

function openUpstream(
  url: string,
  subscriptionId: string,
  filters: readonly NostrFilter[],
  handlers: UpstreamHandlers
): Upstream | undefined {
  let socket: WebSocket;
  try {
    socket = new WebSocket(url);
  } catch (error) {
    console.warn(`Relay ${url} could not be opened`, { error });
    return undefined;
  }
  const upstream: Upstream = { url, socket, done: false, failed: false };
  socket.addEventListener("open", () => {
    socket.send(JSON.stringify(["REQ", subscriptionId, ...filters]));
  });
  socket.addEventListener("message", (message) => {
    const frame = decodeRelayFrame(parseFrame(message.data));
    if (!frame) return;
    if (frame.type === "closed") {
      upstream.failed = true;
      handlers.onDown(upstream, frame.reason || `${url} closed the subscription`);
      return;
    }
    handlers.onFrame(upstream, frame);
  });
  socket.addEventListener("error", () => {
    upstream.failed = true;
    handlers.onDown(upstream, `${url} failed to connect`);
  });
  socket.addEventListener("close", () => handlers.onDown(upstream, `${url} closed the connection`));
  return upstream;
}

/** One-shot read: REQ, collect until every relay EOSEs (or `timeoutMs` elapses). */
export function relayQuery(
  relays: readonly string[],
  filters: readonly NostrFilter[],
  options: RelayQueryOptions = {}
): Promise<RelayEventResult[]> {
  const timeoutMs = options.timeoutMs ?? DEFAULT_QUERY_TIMEOUT_MS;
  const subscriptionId = nextSubscriptionId();
  if (!relays.length) return Promise.reject(new Error("No relays are configured for this build."));

  return new Promise((resolve, reject) => {
    const upstreams: Upstream[] = [];
    const seen = new Set<string>();
    const events = new Map<string, RelayEventResult>();
    let inFlight = 0;
    let finished = false;

    const finish = () => {
      if (finished || inFlight > 0) return;
      finished = true;
      clearTimeout(timer);
      closeAll(upstreams, subscriptionId);
      const answered = upstreams.filter((upstream) => !upstream.failed).length;
      if (events.size) resolve([...events.values()]);
      else if (!answered) reject(new Error(`No relay answered (${relays.join(", ")}).`));
      else resolve([]);
    };

    const timer = setTimeout(() => {
      console.warn("Relay query timed out", { relays, timeoutMs });
      finish();
    }, timeoutMs);

    const accept = (upstream: Upstream, event: NostrEvent) => {
      if (seen.has(event.id)) return;
      seen.add(event.id);
      inFlight += 1;
      void isTrustworthyEvent(event, filters)
        .then((trusted) => {
          if (trusted) events.set(event.id, { event, sidecar: { relayHints: [upstream.url] } });
          else console.warn("Dropped unverifiable relay event", { relay: upstream.url, id: event.id });
        })
        .finally(() => {
          inFlight -= 1;
          if (upstreams.every((current) => current.done)) finish();
        });
    };

    const markDone = (upstream: Upstream, reason: string, failed: boolean) => {
      if (upstream.done) return;
      upstream.done = true;
      upstream.failed ||= failed;
      console.warn(`Relay read ended early: ${reason}`, { relay: upstream.url });
      if (upstreams.length && upstreams.every((current) => current.done)) finish();
    };

    for (const url of relays) {
      const upstream = openUpstream(url, subscriptionId, filters, {
        onFrame: (current, frame) => {
          if (frame.type === "event") accept(current, frame.event);
          else if (frame.type === "eose") {
            current.done = true;
            if (upstreams.every((other) => other.done)) finish();
          } else if (frame.type === "notice") {
            console.warn(`Relay notice from ${current.url}: ${frame.reason}`);
          }
        },
        onDown: (current, reason) => markDone(current, reason, true)
      });
      if (upstream) upstreams.push(upstream);
    }
    if (!upstreams.length) {
      clearTimeout(timer);
      finished = true;
      reject(new Error(`No relay could be reached (${relays.join(", ")}).`));
    }
  });
}

/** Live read: keep the REQ open and stream trusted events until `close()`. */
export function relayWatch(
  relays: readonly string[],
  filters: readonly NostrFilter[],
  handlers: RelayWatchHandlers
): EventSubscription {
  const subscriptionId = nextSubscriptionId();
  const upstreams: Upstream[] = [];
  const seen = new Set<string>();
  let closed = false;

  const shutdown = (reason: string) => {
    if (closed) return;
    closed = true;
    closeAll(upstreams, subscriptionId);
    handlers.onClosed?.(reason);
  };

  const accept = (upstream: Upstream, event: NostrEvent) => {
    if (closed || seen.has(event.id)) return;
    seen.add(event.id);
    void isTrustworthyEvent(event, filters).then((trusted) => {
      if (closed) return;
      if (trusted) handlers.onEvent({ event, sidecar: { relayHints: [upstream.url] } });
      else console.warn("Dropped unverifiable relay event", { relay: upstream.url, id: event.id });
    });
  };

  const markDown = (upstream: Upstream, reason: string) => {
    if (upstream.done) return;
    upstream.done = true;
    if (upstreams.every((current) => current.done)) shutdown(reason);
  };

  for (const url of relays) {
    const upstream = openUpstream(url, subscriptionId, filters, {
      onFrame: (current, frame) => {
        if (frame.type === "event") accept(current, frame.event);
        else if (frame.type === "notice") handlers.onNotice?.(`${current.url}: ${frame.reason}`);
      },
      onDown: (current, reason) => markDown(current, reason)
    });
    if (upstream) upstreams.push(upstream);
  }

  if (!upstreams.length) shutdown("No default relay could be reached.");
  return {
    close: () => {
      closed = true;
      closeAll(upstreams, subscriptionId);
    }
  };
}
